import * as z from "zod"

// Schema para autorizações do processo
export const autorizacaoSchema = z.object({
  autorizador_fk: z.number(),
  ordem: z.number().nullable(),
})

// Schema para links do processo
export const linkSchema = z.object({
  link: z.string().url("Link deve ser uma URL válida"),
  titulo: z.string(), // Título descritivo para o link
}).partial({ titulo: true }) // Torna titulo opcional de forma compatível com exactOptionalPropertyTypes

// Schema para criação de processo baseado na API
export const processFormSchema = z.object({
  nome_processo: z.string().min(1, "Nome do processo é obrigatório").min(3, "Nome deve ter pelo menos 3 caracteres").max(255, "Nome não pode exceder 255 caracteres"),
  tipo: z
    .string()
    .max(255, "Tipo não pode exceder 255 caracteres")
    .optional()
    .or(z.literal("")),
  prioridade_baixa: z.coerce.number().min(1, "Deve ter pelo menos 1 dia útil").max(365, "Não pode exceder 365 dias"),
  prioridade_media: z.coerce.number().min(1, "Deve ter pelo menos 1 dia útil").max(365, "Não pode exceder 365 dias"),
  prioridade_alta: z.coerce.number().min(1, "Deve ter pelo menos 1 dia útil").max(365, "Não pode exceder 365 dias"),
  prioridade_urgente: z.coerce.number().min(1, "Deve ter pelo menos 1 dia útil").max(365, "Não pode exceder 365 dias"),
  setor_processo_fk: z.string().min(1, "Setor é obrigatório"),
  descricao: z.string().min(1, "Descrição é obrigatória").min(10, "Descrição deve ter pelo menos 10 caracteres"),
  instrucoes: z.string().optional(),
  links: z.array(linkSchema).optional(),
  requer_autorizacao: z.boolean().default(false),
  autorizacao_sequencial: z.boolean().default(false),
  autorizacoes: z.array(autorizacaoSchema).optional(),
  gestor_demandante: z.boolean().default(false),
  gestor_demandado: z.boolean().default(false),
  executivo_demandante: z.boolean().default(false),
  executivo_demandado: z.boolean().default(false),
  equipe: z.boolean().default(true), // Por padrão, processos podem ser abertos por equipe
}).refine((data) => {
  // SOMENTE valida se requer_autorizacao estiver ATIVO
  if (!data.requer_autorizacao) {
    return true // Se autorização não é obrigatória, não precisa de autorizadores
  }

  // Se requer autorização, deve ter pelo menos 1 autorizador
  const hasGestoresOuExecutivos =
    data.gestor_demandante ||
    data.gestor_demandado ||
    data.executivo_demandante ||
    data.executivo_demandado

  const temAutorizadoresExtras = (data.autorizacoes ?? []).length > 0

  return hasGestoresOuExecutivos || temAutorizadoresExtras
}, {
  message: "Selecione pelo menos um autorizador quando a autorização for obrigatória",
  path: ["autorizacoes"],
}).refine((data) => {
  // SOMENTE valida se AMBOS estiverem ativos
  if (!data.requer_autorizacao || !data.autorizacao_sequencial) {
    return true // Se não requer autorização sequencial, não precisa validar ordem
  }

  // Se é sequencial, validar que as ordens são consecutivas começando do 1
  const autorizacoes = data.autorizacoes ?? []
  const temAutorizacoesExtras = autorizacoes.length > 0
  const hasGestoresOuExecutivos =
    data.gestor_demandante ||
    data.gestor_demandado ||
    data.executivo_demandante ||
    data.executivo_demandado

  if (!temAutorizacoesExtras) {
    return hasGestoresOuExecutivos
  }

  const ordens = autorizacoes
    .map(a => a.ordem)
    .filter(o => o !== null)
    .sort((a, b) => a! - b!)

  if (ordens.length === 0) {
    return hasGestoresOuExecutivos
  }

  // Verificar se começa em 1 e é consecutivo
  for (let i = 0; i < ordens.length; i++) {
    if (ordens[i] !== i + 1) return false
  }

  return true
}, {
  message: "A ordem das autorizações deve ser consecutiva começando em 1",
  path: ["autorizacoes"],
})

export type ProcessFormValues = z.infer<typeof processFormSchema>

// Schema para a API request
export const createProcessoRequest = z.object({
  nome_processo: z.string(),
  tipo: z.string().optional(),
  prioridade_baixa: z.number(),
  prioridade_media: z.number(),
  prioridade_alta: z.number(),
  prioridade_urgente: z.number(),
  setor_processo_fk: z.number(),
  descricao: z.string(),
  instrucoes: z.string().optional(),
  links: z.array(linkSchema).optional(),
  autorizacoes: z.array(autorizacaoSchema).optional(),
  gestor_demandante: z.boolean().optional(),
  gestor_demandado: z.boolean().optional(),
  executivo_demandante: z.boolean().optional(),
  executivo_demandado: z.boolean().optional(),
  equipe: z.boolean().optional(),
  template_schema: z.record(z.string(), z.unknown()).optional(), // JSON Schema para templates dinâmicos
})

export type CreateProcessoRequest = z.infer<typeof createProcessoRequest>
